import React, { useState } from 'react'
import { useI18n } from '../i18n'
import { evaluateCondition } from '../utils/conditionEval'
import { normalizeConditionPreviewParams } from '../utils/normalizeConditionPreviewParams'
import { computeMonetizationGatePreview } from '../utils/monetizationPreview'
import { Select, DeleteButton } from './ui'
import ConfirmModal from './ConfirmModal'
import { L2_SCHEMA, L3_SCHEMA } from '../config/graphEntities'

/** Числовые параметры L3 (0–100) — sourced from L3_SCHEMA to avoid duplicates. */

/** L3 keys from schema. */
export const L3_KEYS = L3_SCHEMA.map((r) => r.key)

/** L2 переменные из БД (для условий). */
export const L2_VAR_KEYS = [
  'total_messages_in_chat',
  'total_fan_messages',
  'messages_in_current_session',
  'session_length_minutes',
  'messages_7d',
  'days_since_last_message',
  'total_spend',
  'spend_30d',
  'purchase_count',
  'ever_bought',
  'days_since_last_purchase',
  'offer_accept_rate',
  'ghost_after_offer_rate',
  'recent_offer_rejected',
  'messages_since_offer',
  'preferred_tone',
  'buyer_style_hint',
  'conversation_style_hint',
  /* L4: стадии и флаги из fan_bot_profiles */
  'relationship_stage',
  'monetization_stage',
  'conversation_state',
  'sale_script_allowed',
  'direct_offer_allowed',
  'upsell_allowed',
  'cooldown_after_offer',
  /* Core / graph_transitions evaluation (graph_config_service transition_ctx) */
  'current_state',
  'postbuy_phase',
  'proactive_trigger',
  'fan_came_online',
  'fan_is_online',
  'fan_started_typing',
  'fan_liked_post',
  'fan_commented_post',
  'physical_item_interest_score',
  'physical_item_active',
  'support_interest_score',
  'support_route_active',
  'dominant_style_score',
  'dominant_style_preference',
  'dominant_style_active',
  'timewaster_strategy_active',
]

/** Тип и значения параметра: { type: 'int'|'float'|'bool'|'enum', values?: string[] }. */
function getParamMeta(key) {
  const l2 = L2_SCHEMA.find((r) => r.key === key)
  if (l2) {
    if (l2.type === 'bool') return { type: 'bool' }
    if (l2.type === 'enum' && l2.range) {
      const values = l2.range.split(',').map((s) => s.trim()).filter(Boolean)
      return { type: 'enum', values }
    }
    return { type: l2.type === 'float' ? 'float' : 'int' }
  }
  if (L3_KEYS.includes(key)) return { type: 'int' }
  return { type: 'int' }
}

/** Все ключи для условий: L2 + L3 (L1 не используется в условиях). */
export const ALL_CONDITION_PARAM_KEYS = [...L2_VAR_KEYS, ...L3_KEYS]

/** Уровень ключа: l2=2, l3=3, l4=4 (L1 не в условиях). l4KeySet — ключи из variable_definitions. */
function getKeyLevel(key, l4KeySet = null) {
  if (l4KeySet && l4KeySet.includes(key)) return 4
  if (L2_VAR_KEYS.includes(key)) return 2
  if (L3_KEYS.includes(key)) return 3
  return 4
}

/** Сортировка ключей по уровню L2 → L3 → L4, внутри уровня — по имени. */
function sortKeysByLevel(keys, l4KeySet = null) {
  return [...keys].sort((a, b) => {
    const la = getKeyLevel(a, l4KeySet)
    const lb = getKeyLevel(b, l4KeySet)
    if (la !== lb) return la - lb
    return String(a).localeCompare(String(b))
  })
}

const OPS_NUMERIC = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
]

const OPS_EQ_NEQ = [
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
]

function emptyCond(keys = ALL_CONDITION_PARAM_KEYS) {
  const param = keys[0] || L3_KEYS[0]
  const meta = getParamMeta(param)
  let value
  if (meta.type === 'bool') value = false
  else if (meta.type === 'enum' && meta.values?.length) value = meta.values[0]
  else value = 0
  return { param, op: meta.type === 'int' || meta.type === 'float' ? 'gte' : 'eq', value }
}

/** Проверяет, простое ли условие (param + op + value). */
function isSimple(c) {
  return c && typeof c === 'object' && 'param' in c && 'op' in c
}

/**
 * Преобразует вложенную структуру backend в плоский формат для UI.
 * { logic: "or", conditions: [{ logic: "and", conditions: [c1,c2] }, c3] } → { conditions, logicBetween }
 *
 * @param {object} [opts]
 * @param {boolean} [opts.allowPlainBoolean] — если true, ``true``/``false``/отсутствие ключа не сбрасываются в пустое DSL.
 */
function flatten(cond, opts = {}) {
  const { allowPlainBoolean = false } = opts
  if (allowPlainBoolean && typeof cond === 'boolean') {
    return { mode: 'bool', boolValue: cond, conditions: [], logicBetween: [] }
  }
  if (allowPlainBoolean && (cond === undefined || cond === null)) {
    return { mode: 'unset', conditions: [], logicBetween: [] }
  }
  if (!cond || typeof cond !== 'object') return { mode: 'dsl', conditions: [emptyCond()], logicBetween: [] }
  if (isSimple(cond)) return { mode: 'dsl', conditions: [cond], logicBetween: [] }

  const logic = (cond.logic || 'and').toLowerCase()
  const sub = cond.conditions || []
  if (sub.length === 0) return { mode: 'dsl', conditions: [], logicBetween: [] }

  const flatConditions = []
  const flatLogicBetween = []

  for (let i = 0; i < sub.length; i++) {
    const s = sub[i]
    if (isSimple(s)) {
      flatConditions.push(s)
    } else if (s.logic && Array.isArray(s.conditions)) {
      const inner = s.conditions
      for (let j = 0; j < inner.length; j++) {
        flatConditions.push(isSimple(inner[j]) ? inner[j] : emptyCond())
        if (j < inner.length - 1) flatLogicBetween.push((s.logic || 'and').toLowerCase())
      }
    }
    if (i < sub.length - 1) flatLogicBetween.push(logic)
  }

  if (flatConditions.length === 0) return { mode: 'dsl', conditions: [], logicBetween: [] }
  return { mode: 'dsl', conditions: flatConditions, logicBetween: flatLogicBetween }
}

/**
 * Преобразует плоский формат в вложенную структуру для backend.
 * conditions [c1,c2,c3], logicBetween ['and','or'] → (c1 AND c2) OR c3
 * AND связывает сильнее (группирует условия), OR разделяет группы.
 */
function buildNested(conditions, logicBetween) {
  if (!conditions || conditions.length === 0) return { logic: 'and', conditions: [emptyCond()] }
  if (conditions.length === 1) return conditions[0]

  const groups = []
  let current = [conditions[0]]

  for (let i = 1; i < conditions.length; i++) {
    const conn = logicBetween[i - 1] || 'and'
    if (conn === 'or') {
      // OR разделяет группы: закрываем текущую AND-группу
      groups.push(current.length === 1 ? current[0] : { logic: 'and', conditions: current })
      current = [conditions[i]]
    } else {
      // AND связывает условия в одну группу
      current.push(conditions[i])
    }
  }
  groups.push(current.length === 1 ? current[0] : { logic: 'and', conditions: current })

  if (groups.length === 1) return typeof groups[0] === 'object' && groups[0].logic ? groups[0] : { logic: 'and', conditions: conditions }
  return { logic: 'or', conditions: groups }
}

/**
 * Плоский список условий. Между каждой парой — свой AND или OR.
 * paramKeys — список доступных параметров (по умолчанию ALL_CONDITION_PARAM_KEYS).
 */
/** Словарь key → name для L4 (отображается независимо от языка). */
function getL4NameMap(l4Definitions) {
  if (!Array.isArray(l4Definitions)) return {}
  const map = {}
  for (const d of l4Definitions) {
    if (d?.key && d?.name != null && String(d.name).trim()) {
      map[d.key] = String(d.name).trim()
    }
  }
  return map
}

/**
 * fixedFirstRow — фиксированная первая строка (напр. "Молчание ≥ N ч") и "И" после неё.
 * { label, value, onChange, unit, min, max }
 * allowEmptyConditions — при fixedFirstRow разрешить удалить все доп. условия (оставить только часы).
 * confirmRemove — показывать модалку подтверждения перед удалением условия.
 */
export default function ConditionBuilder({
  condition,
  onChange,
  paramKeys = ALL_CONDITION_PARAM_KEYS,
  l4Keys: l4KeySet = null,
  l4Definitions = [],
  allowOr = true,
  fixedFirstRow = null,
  allowEmptyConditions = false,
  confirmRemove = false,
  showEvalPreview = false,
  allowPlainBoolean = false,
  graphTransitions = null,
  showMonetizationGatePreview = false,
}) {
  const { t } = useI18n()
  const [pendingRemoveIdx, setPendingRemoveIdx] = useState(null)
  const [previewJson, setPreviewJson] = useState(
    '{\n  "conversation_state": "OFFER",\n  "engagement_score": 50\n}',
  )
  const [previewPhase, setPreviewPhase] = useState('after')
  const [previewOutcome, setPreviewOutcome] = useState(null)
  const [previewErr, setPreviewErr] = useState(null)
  const [monetizationGatePreview, setMonetizationGatePreview] = useState(null)
  const flat = flatten(condition, { allowPlainBoolean })
  const rawKeys = (paramKeys != null && Array.isArray(paramKeys)) ? paramKeys : ALL_CONDITION_PARAM_KEYS
  const keys = sortKeysByLevel(rawKeys, l4KeySet)
  const l4NameMap = getL4NameMap(l4Definitions)

  if (allowPlainBoolean && flat.mode === 'bool') {
    return (
      <div className="condition-builder condition-builder-plain-bool">
        <Select
          options={[
            { value: 'true', label: t('settings.condition.plainBoolTrue') },
            { value: 'false', label: t('settings.condition.plainBoolFalse') },
          ]}
          value={String(flat.boolValue)}
          onChange={(v) => onChange(v === 'true')}
          className="condition-param custom-select--param"
          dropdownAutoWidth
        />
        <button
          type="button"
          className="condition-add"
          style={{ marginTop: '0.75rem' }}
          onClick={() => onChange({ logic: 'and', conditions: [emptyCond(keys)] })}
        >
          {t('settings.condition.switchToExpression')}
        </button>
        <button
          type="button"
          className="modal-btn modal-btn-secondary"
          style={{ marginTop: '0.5rem', display: 'block' }}
          onClick={() => onChange(null)}
        >
          {t('settings.condition.clearToBackendDefault')}
        </button>
      </div>
    )
  }

  if (allowPlainBoolean && flat.mode === 'unset') {
    return (
      <div className="condition-builder condition-builder-plain-bool">
        <p className="settings-hint" style={{ marginBottom: '0.75rem' }}>
          {t('settings.condition.plainBoolDefaultHint')}
        </p>
        <div className="settings-edge-actions" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="modal-btn modal-btn-secondary" onClick={() => onChange(true)}>
            {t('settings.condition.plainBoolForceTrue')}
          </button>
          <button type="button" className="modal-btn modal-btn-secondary" onClick={() => onChange(false)}>
            {t('settings.condition.plainBoolForceFalse')}
          </button>
          <button
            type="button"
            className="condition-add"
            onClick={() => onChange({ logic: 'and', conditions: [emptyCond(keys)] })}
          >
            {t('settings.condition.switchToExpression')}
          </button>
        </div>
      </div>
    )
  }

  const { conditions, logicBetween } = flat

  function updateCond(idx, updates) {
    const next = [...conditions]
    next[idx] = { ...(next[idx] || emptyCond()), ...updates }
    const lb = logicBetween.slice(0, next.length - 1)
    while (lb.length < next.length - 1) lb.push('and')
    onChange(buildNested(next, allowOr ? lb : lb.map(() => 'and')))
  }

  function updateLogicBetween(idx, val) {
    if (!allowOr) return
    const lb = [...logicBetween]
    while (lb.length < conditions.length - 1) lb.push('and')
    lb[idx] = val
    onChange(buildNested(conditions, lb))
  }

  function addCond() {
    const next = [...conditions, emptyCond(keys)]
    const lb = logicBetween.slice()
    lb.push('and')
    onChange(buildNested(next, allowOr ? lb : lb.map(() => 'and')))
  }

  function removeCond(idx) {
    const next = conditions.filter((_, i) => i !== idx)
    if (next.length === 0) {
      onChange(allowEmptyConditions ? { logic: 'and', conditions: [] } : { logic: 'and', conditions: [emptyCond()] })
    } else if (next.length === 1) {
      onChange(buildNested(next, []))
    } else {
      const lb = logicBetween.filter((_, i) => i !== idx)
      onChange(buildNested(next, lb))
    }
    setPendingRemoveIdx(null)
  }

  return (
    <div className="condition-builder condition-builder-flat">
      {fixedFirstRow && (
        <>
          <div className="condition-row condition-row--fixed">
            <span className="condition-fixed-label">{fixedFirstRow.label}</span>
            <input
              type="number"
              min={fixedFirstRow.min ?? 1}
              max={fixedFirstRow.max ?? 720}
              step={fixedFirstRow.step ?? 1}
              value={fixedFirstRow.value ?? 24}
              onChange={(e) => {
                const v = parseFloat(e.target.value)
                const minV = fixedFirstRow.min ?? 1
                const maxV = fixedFirstRow.max ?? 720
                const next = isNaN(v) ? (fixedFirstRow.value ?? 24) : Math.max(minV, Math.min(maxV, v))
                fixedFirstRow.onChange(next)
              }}
              className="condition-value condition-value--hours"
            />
            {Array.isArray(fixedFirstRow.unitOptions) && fixedFirstRow.unitOptions.length > 0 && typeof fixedFirstRow.onUnitChange === 'function' ? (
              <select
                className="condition-fixed-unit condition-fixed-unit--select"
                value={fixedFirstRow.unit ?? fixedFirstRow.unitOptions[0]?.value}
                onChange={(e) => fixedFirstRow.onUnitChange(e.target.value)}
              >
                {fixedFirstRow.unitOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              fixedFirstRow.unit && <span className="condition-fixed-unit">{fixedFirstRow.unit}</span>
            )}
          </div>
          <div className="condition-connector">
            <span className="condition-logic-static">{t('settings.condition.and')}</span>
          </div>
        </>
      )}
      {conditions.map((cond, idx) => (
        <React.Fragment key={idx}>
          <div className="condition-row">
            <Select
              options={keys.map((p) => {
                const lvl = getKeyLevel(p, l4KeySet)
                // L4: всегда показывать name из БД (без i18n); иначе — перевод или key
                const label = lvl === 4 && l4NameMap[p] ? l4NameMap[p] : (t(`params.${p}`) || p)
                const level = lvl === 2 ? 'l2' : lvl === 3 ? 'l3' : 'l4'
                return { value: p, label, level }
              })}
              value={cond?.param && keys.includes(cond.param) ? cond.param : keys[0]}
              onChange={(v) => {
                const prev = cond?.param
                const meta = getParamMeta(v)
                const updates = { param: v }
                if (meta.type === 'bool') {
                  updates.op = 'eq'
                  updates.value = false
                } else if (meta.type === 'enum' && meta.values?.length) {
                  updates.op = 'eq'
                  updates.value = meta.values[0]
                } else {
                  updates.op = 'gte'
                  updates.value = 0
                }
                updateCond(idx, updates)
              }}
              className="condition-param custom-select--param"
              dropdownAutoWidth
            />
            <Select
              options={(getParamMeta(cond?.param).type === 'int' || getParamMeta(cond?.param).type === 'float') ? OPS_NUMERIC : OPS_EQ_NEQ}
              value={
                (() => {
                  const t = getParamMeta(cond?.param).type
                  if (t === 'int' || t === 'float') return cond?.op || 'gte'
                  return ['eq', 'neq'].includes(cond?.op) ? cond.op : 'eq'
                })()
              }
              onChange={(v) => updateCond(idx, { op: v })}
              className="condition-op custom-select--op"
            />
            {(() => {
              const meta = getParamMeta(cond?.param)
              const isFloat = meta.type === 'float'
              if (meta.type === 'bool') {
                return (
                  <Select
                    options={[
                      { value: true, label: t('settings.variables.resultTrue') },
                      { value: false, label: t('settings.variables.resultFalse') },
                    ]}
                    value={cond?.value === true || cond?.value === 'true' || cond?.value === 1}
                    onChange={(v) => updateCond(idx, { value: v })}
                    className="condition-value condition-value--select"
                  />
                )
              }
              if (meta.type === 'enum' && meta.values?.length) {
                return (
                  <Select
                    options={meta.values.map((val) => ({ value: val, label: val }))}
                    value={meta.values.includes(cond?.value) ? cond.value : meta.values[0]}
                    onChange={(v) => updateCond(idx, { value: v })}
                    className="condition-value condition-value--select"
                    dropdownAutoWidth
                  />
                )
              }
              return (
                <input
                  type="number"
                  min={0}
                  step={isFloat ? 0.01 : 1}
                  value={cond?.value ?? 0}
                  onChange={(e) => {
                    const v = e.target.value
                    const num = isFloat ? parseFloat(v) : parseInt(v, 10)
                    updateCond(idx, { value: isNaN(num) ? 0 : num })
                  }}
                  className="condition-value"
                />
              )
            })()}
            {(conditions.length > 1 || (allowEmptyConditions && conditions.length >= 1)) && (
              <DeleteButton
                onClick={() => (confirmRemove ? setPendingRemoveIdx(idx) : removeCond(idx))}
                title={t('common.delete')}
              />
            )}
          </div>
          {idx < conditions.length - 1 && (
            <div className="condition-connector">
              {allowOr ? (
                <Select
                  options={[
                    { value: 'and', label: t('settings.condition.and') },
                    { value: 'or', label: t('settings.condition.or') },
                  ]}
                  value={logicBetween[idx] || 'and'}
                  onChange={(v) => updateLogicBetween(idx, v)}
                  className="condition-logic custom-select--logic"
                  dropdownAutoWidth
                />
              ) : (
                <span className="condition-logic-static">{t('settings.condition.and')}</span>
              )}
            </div>
          )}
        </React.Fragment>
      ))}
      <button type="button" className="condition-add" onClick={addCond}>
        {t('settings.condition.addCondition')}
      </button>
      {showEvalPreview && (
        <div className="condition-preview" style={{ marginTop: '1rem' }}>
          <label className="media-edit-label">{t('settings.condition.previewTitle')}</label>
          <p className="settings-hint" style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}>
            {t('settings.condition.previewPasteHint')}
          </p>
          <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className="settings-hint">{t('settings.condition.previewPhaseLabel')}</span>
            <Select
              options={[
                { value: 'after', label: t('settings.condition.previewPhaseAfter') },
                { value: 'before', label: t('settings.condition.previewPhaseBefore') },
              ]}
              value={previewPhase}
              onChange={(v) => {
                setPreviewPhase(v)
                setPreviewOutcome(null)
                setPreviewErr(null)
                setMonetizationGatePreview(null)
              }}
              dropdownAutoWidth
            />
          </div>
          <textarea
            className="setting-value"
            style={{ width: '100%', minHeight: '120px', fontFamily: 'monospace' }}
            value={previewJson}
            onChange={(e) => {
              setPreviewJson(e.target.value)
              setPreviewOutcome(null)
              setPreviewErr(null)
              setMonetizationGatePreview(null)
            }}
          />
          <button
            type="button"
            className="modal-btn modal-btn-secondary"
            style={{ marginTop: '0.5rem' }}
            onClick={() => {
              try {
                const parsed = JSON.parse(previewJson || '{}')
                const p = normalizeConditionPreviewParams(parsed, {
                  phase: previewPhase === 'before' ? 'before' : 'after',
                })
                setPreviewErr(null)
                setPreviewOutcome(evaluateCondition(condition, p))
                if (showMonetizationGatePreview && graphTransitions && typeof graphTransitions === 'object') {
                  setMonetizationGatePreview(computeMonetizationGatePreview(graphTransitions, p))
                } else {
                  setMonetizationGatePreview(null)
                }
              } catch {
                setPreviewErr(t('settings.condition.previewInvalidJson'))
                setPreviewOutcome(null)
                setMonetizationGatePreview(null)
              }
            }}
          >
            {t('settings.condition.previewEvaluate')}
          </button>
          {previewErr && <p className="settings-error">{previewErr}</p>}
          {previewOutcome !== null && !previewErr && (
            <p className="settings-hint">
              {t('settings.condition.previewResult')}: <strong>{String(previewOutcome)}</strong>
            </p>
          )}
          {monetizationGatePreview && !previewErr && (
            <div className="settings-hint" style={{ marginTop: '0.75rem' }}>
              <p style={{ margin: '0 0 0.35rem' }}>{t('settings.condition.monetizationGateTitle')}</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                <li>
                  {t('settings.condition.monetizationPostbuyBlock')}:{' '}
                  <strong>{String(monetizationGatePreview.postbuy_block)}</strong>
                </li>
                <li>
                  {t('settings.condition.monetizationRunMonetization')}:{' '}
                  <strong>{String(monetizationGatePreview.run_monetization)}</strong>
                </li>
                <li>
                  {t('settings.condition.monetizationAllowOfferEffective')}:{' '}
                  <strong>{String(monetizationGatePreview.allow_offer_effective)}</strong>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
      {confirmRemove && (
        <ConfirmModal
          open={pendingRemoveIdx != null}
          title={t('settings.deleteConditionTitle')}
          message={t('settings.deleteConditionMessage')}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={() => pendingRemoveIdx != null && removeCond(pendingRemoveIdx)}
          onCancel={() => setPendingRemoveIdx(null)}
          danger
        />
      )}
    </div>
  )
}
